import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ReactPaginate from 'react-paginate';
import styled from 'styled-components';
import type { RootState } from '../../../state/store';
import { usePromotionEngineReport } from '../hooks/usePromotionEngine';
import type { ReportRow } from '../types';
import { EmptyPeriodState, EngineSkeleton, engineTableCss, money, roi } from './shared';

type SortKey =
  | 'promotion_name'
  | 'location_name'
  | 'orders'
  | 'promo_discount'
  | 'promo_revenue'
  | 'gross_margin'
  | 'margin_impact'
  | 'promo_roi';
type SortDir = 'asc' | 'desc';
type FilterOption = { id: number; name: string };

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const COLUMNS: {
  key: SortKey;
  label: string;
  className?: string;
  filterable?: boolean;
  hint?: string;
}[] = [
  { key: 'promotion_name', label: 'Promotion', filterable: true },
  { key: 'location_name', label: 'Location', className: 'locs', filterable: true },
  { key: 'orders', label: 'Orders', className: 'num' },
  { key: 'promo_discount', label: 'Discount', className: 'num' },
  { key: 'promo_revenue', label: 'Promo revenue', className: 'num' },
  {
    key: 'gross_margin',
    label: 'Gross margin',
    className: 'num',
    hint: 'Promo-ticket net sales minus COGS from bronze computed cost. Not discount spend.'
  },
  {
    key: 'promo_roi',
    label: 'ROI',
    className: 'num',
    hint: 'Promo margin ÷ discount for this period. This is an estimate, not incremental lift.'
  }
];

const rowSpans = (rows: ReportRow[]) => {
  const spans = Array(rows.length).fill(0);
  let i = 0;
  while (i < rows.length) {
    let j = i + 1;
    while (j < rows.length && rows[j].promotion_id === rows[i].promotion_id) j += 1;
    spans[i] = j - i;
    i = j;
  }
  return spans;
};

const ReportTab = () => {
  const selectedLocationIds = useSelector((state: RootState) => state.filters.locations || []);
  const companyLocations = useSelector((state: RootState) => state.locations || []);
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('promotion_name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [promoIds, setPromoIds] = useState<number[]>([]);
  const [locIds, setLocIds] = useState<number[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const applySearch = () => setQ(qInput.trim());
  const clearSearch = () => {
    setQInput('');
    setQ('');
  };

  useEffect(() => {
    setPage(0);
  }, [q, sortKey, sortDir, pageSize, promoIds, locIds]);

  const query = useMemo(
    () => ({
      pagination: `${page * pageSize + 1}:${page * pageSize + pageSize}`,
      sort: sortKey,
      sort_dir: sortDir,
      q,
      promotion_ids: promoIds,
      report_location_ids: locIds
    }),
    [page, pageSize, sortKey, sortDir, q, promoIds, locIds]
  );

  const { data, error, isLoading, isValidating } = usePromotionEngineReport(true, query);
  const currency = data?.currency_code || '';
  const rows = data?.rows || [];
  const total = data?.pagination?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const spans = useMemo(() => rowSpans(rows), [rows]);
  const groupOnPromo = sortKey === 'promotion_name';

  const locationOptions = useMemo<FilterOption[]>(() => {
    const selected = new Set((selectedLocationIds as number[]).map(Number));
    return (companyLocations as Array<{ id: number; name?: string; location_name?: string }>)
      .filter((l) => selected.has(Number(l.id)))
      .map((l) => ({ id: Number(l.id), name: l.name || l.location_name || `Location ${l.id}` }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [companyLocations, selectedLocationIds]);

  const onSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'promotion_name' || key === 'location_name' ? 'asc' : 'desc');
  };

  if (!data && (isLoading || isValidating)) {
    return <EngineSkeleton tab="report" />;
  }

  if (error) {
    return <ErrorBox>Unable to load the report.</ErrorBox>;
  }

  if (!data || (total === 0 && !q && promoIds.length === 0 && locIds.length === 0)) {
    return <EmptyPeriodState />;
  }

  const from = total === 0 ? 0 : safePage * pageSize + 1;
  const to = Math.min(total, safePage * pageSize + pageSize);

  return (
    <Wrap $busy={isValidating}>
      <Toolbar>
        <Intro>One row per promotion and location for the selected outlets and dates.</Intro>
        <SearchForm
          onSubmit={(e) => {
            e.preventDefault();
            applySearch();
          }}
        >
          <Search
            value={qInput}
            placeholder="Search promotion or location"
            onChange={(e) => setQInput(e.target.value)}
          />
          <SearchBtn type="submit">Search</SearchBtn>
          {q.length > 0 && (
            <Ghost type="button" onClick={clearSearch}>
              Clear
            </Ghost>
          )}
        </SearchForm>
      </Toolbar>
      <Meta>
        {total.toLocaleString()} rows
        {promoIds.length > 0 && ` · ${promoIds.length} promotions`}
        {locIds.length > 0 && ` · ${locIds.length} locations`}
      </Meta>
      <Panel>
        <Table>
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th key={col.key} className={col.className}>
                  <HeadCell
                    label={col.label}
                    hint={col.hint}
                    align={col.className === 'num' ? 'right' : 'left'}
                    sortKey={col.key}
                    activeKey={sortKey}
                    sortDir={sortDir}
                    onSort={onSort}
                    filterable={col.filterable}
                    options={
                      col.key === 'promotion_name'
                        ? data.filter_options?.promotions || []
                        : locationOptions
                    }
                    selected={col.key === 'promotion_name' ? promoIds : locIds}
                    onFilter={col.key === 'promotion_name' ? setPromoIds : setLocIds}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8}>No rows match that search or filter.</td>
              </tr>
            ) : (
              rows.map((r, index) => {
                const span = groupOnPromo ? spans[index] : 1;
                const showPromo = !groupOnPromo || span > 0;
                return (
                  <tr key={`${r.promotion_id}-${r.location_id}-${index}`}>
                    {showPromo && (
                      <td rowSpan={groupOnPromo ? span : 1} className="promo">
                        {r.promotion_name}
                      </td>
                    )}
                    <td className="locs">{r.location_name}</td>
                    <td className="num">{r.orders.toLocaleString()}</td>
                    <td className="num">{money(r.promo_discount, currency)}</td>
                    <td className="num">{money(r.promo_revenue, currency)}</td>
                    <td className={`num ${(r.gross_margin ?? r.margin_impact) < 0 ? 'neg' : 'pos'}`}>
                      {money(r.gross_margin ?? r.margin_impact, currency)}
                    </td>
                    <td className={`num ${r.promo_roi < 0 ? 'neg' : 'pos'}`}>
                      {roi(r.promo_roi)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </Panel>
      <Pager>
        <PageInfo>
          Showing {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
        </PageInfo>
        <PageSizeLabel>
          Rows
          <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </PageSizeLabel>
        {totalPages > 1 && (
          <ReactPaginate
            pageCount={totalPages}
            forcePage={safePage}
            marginPagesDisplayed={1}
            pageRangeDisplayed={3}
            breakLabel="..."
            onPageChange={(item) => setPage(item.selected)}
            containerClassName="pagination"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            breakClassName="page-item"
            breakLinkClassName="page-link"
            activeClassName="active"
            disabledClassName="disabled"
            previousLabel="Prev"
            nextLabel="Next"
            disableInitialCallback
          />
        )}
      </Pager>
    </Wrap>
  );
};

const HeadCell = ({
  label,
  hint,
  align,
  sortKey,
  activeKey,
  sortDir,
  onSort,
  filterable,
  options = [],
  selected = [],
  onFilter
}: {
  label: string;
  hint?: string;
  align: 'left' | 'right';
  sortKey: SortKey;
  activeKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  filterable?: boolean;
  options?: FilterOption[];
  selected?: number[];
  onFilter?: (values: number[]) => void;
}) => (
  <HeadWrap $align={align}>
    <SortBtn type="button" title={hint} onClick={() => onSort(sortKey)}>
      {label}
      {hint && <HintMark title={hint}>i</HintMark>}
      <SortMark $on={activeKey === sortKey}>
        {activeKey === sortKey ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
      </SortMark>
    </SortBtn>
    {filterable && onFilter && (
      <ColumnFilter options={options} selected={selected} onChange={onFilter} />
    )}
  </HeadWrap>
);

const ColumnFilter = ({
  options,
  selected,
  onChange
}: {
  options: FilterOption[];
  selected: number[];
  onChange: (values: number[]) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const matches = options.filter((o) => o.name.toLowerCase().includes(search.trim().toLowerCase()));

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (id: number) => {
    onChange(selected.includes(id) ? selected.filter((v) => v !== id) : [...selected, id]);
  };

  return (
    <FilterRoot ref={rootRef}>
      <FilterBtn
        type="button"
        aria-expanded={open}
        $active={selected.length > 0}
        onClick={() => {
          setOpen((v) => !v);
          setSearch('');
        }}
      >
        ▾
      </FilterBtn>
      {open && (
        <FilterPanel>
          <FilterSearch
            autoFocus
            value={search}
            placeholder="Filter…"
            onChange={(e) => setSearch(e.target.value)}
          />
          <FilterList>
            {matches.length === 0 ? (
              <FilterEmpty>No matches</FilterEmpty>
            ) : (
              matches.map((opt) => (
                <label key={opt.id}>
                  <input
                    type="checkbox"
                    checked={selected.includes(opt.id)}
                    onChange={() => toggle(opt.id)}
                  />
                  {opt.name}
                </label>
              ))
            )}
          </FilterList>
          <FilterActions>
            <button type="button" onClick={() => onChange([])}>
              Clear
            </button>
            <button type="button" onClick={() => setOpen(false)}>
              Done
            </button>
          </FilterActions>
        </FilterPanel>
      )}
    </FilterRoot>
  );
};

export default ReportTab;

const Wrap = styled.div<{ $busy?: boolean }>`
  opacity: ${({ $busy }) => ($busy ? 0.72 : 1)};
  transition: opacity 0.15s ease;
`;
const Toolbar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;
const Intro = styled.p`
  font-size: 0.86rem;
  color: ${({ theme }) => theme.text_secondary || theme.text};
  margin: 0;
`;
const SearchForm = styled.form`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;
const Search = styled.input`
  min-width: 220px;
  padding: 7px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border_color};
  background: ${({ theme }) => theme.widget.background};
  color: ${({ theme }) => theme.text};
  font-size: 0.8rem;
  outline: none;
`;
const SearchBtn = styled.button`
  padding: 7px 12px;
  border-radius: 8px;
  border: 0;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 700;
  color: #fff;
  background: ${({ theme }) => theme.primary || theme.primary_color || '#0d8a6a'};
`;
const Ghost = styled.button`
  border: 0;
  background: none;
  padding: 6px 4px;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 700;
  color: ${({ theme }) => theme.text_muted || theme.text};
`;
const HintMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0;
  opacity: 0.7;
  border: 1px solid currentColor;
`;
const Meta = styled.div`
  font-size: 0.74rem;
  font-weight: 600;
  color: ${({ theme }) => theme.text_muted || theme.text};
  margin: 0 0 10px;
`;
const Panel = styled.div`
  background: ${({ theme }) => theme.widget.background};
  border: 1px solid ${({ theme }) => theme.border_color};
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  border-radius: 14px;
  padding: 16px 18px;
  overflow: visible;
`;
const Table = styled.table`
  ${engineTableCss}
  table-layout: fixed;

  th:first-child,
  td.promo {
    width: 24%;
    vertical-align: top;
    padding-top: 14px;
  }

  th.locs,
  td.locs {
    width: 18%;
    padding-left: 12px;
  }

  th {
    position: relative;
    background: ${({ theme }) => theme.widget.background};
  }
`;
const HeadWrap = styled.div<{ $align: 'left' | 'right' }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) => ($align === 'right' ? 'flex-end' : 'flex-start')};
  gap: 4px;
`;
const SortBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  background: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: inherit;
`;
const SortMark = styled.span<{ $on: boolean }>`
  font-size: 0.7rem;
  opacity: ${({ $on }) => ($on ? 1 : 0.35)};
  text-transform: none;
  letter-spacing: 0;
`;
const FilterRoot = styled.div`
  position: relative;
  display: inline-flex;
`;
const FilterBtn = styled.button<{ $active: boolean }>`
  border: 0;
  background: none;
  padding: 0 2px;
  cursor: pointer;
  color: ${({ $active, theme }) => ($active ? theme.primary || '#0d8a6a' : 'inherit')};
  opacity: ${({ $active }) => ($active ? 1 : 0.55)};
  font-size: 0.85rem;
`;
const FilterPanel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 50;
  width: 240px;
  border-radius: 10px;
  background: ${({ theme }) => theme.widget.background};
  border: 1px solid ${({ theme }) => theme.border_color};
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
  text-align: left;
  text-transform: none;
  letter-spacing: 0;
`;
const FilterSearch = styled.input`
  width: calc(100% - 16px);
  margin: 8px;
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border_color};
  background: ${({ theme }) => theme.body};
  color: ${({ theme }) => theme.text};
  font-size: 0.78rem;
  outline: none;
`;
const FilterList = styled.div`
  max-height: 200px;
  overflow: auto;
  padding: 0 8px 8px;

  label {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 4px;
    font-size: 0.78rem;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    cursor: pointer;
  }
`;
const FilterEmpty = styled.div`
  padding: 8px 4px;
  font-size: 0.76rem;
  color: ${({ theme }) => theme.text_muted || theme.text};
`;
const FilterActions = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 10px;
  border-top: 1px solid ${({ theme }) => theme.border || 'rgba(0,0,0,0.08)'};

  button {
    border: 0;
    background: none;
    cursor: pointer;
    font-size: 0.74rem;
    font-weight: 700;
    color: ${({ theme }) => theme.primary || '#0d8a6a'};
  }
`;
const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 14px;

  .pagination {
    display: flex;
    list-style: none;
    gap: 4px;
    margin: 0;
    padding: 0;
  }
  .page-link {
    display: block;
    padding: 6px 10px;
    border-radius: 8px;
    text-decoration: none;
    color: ${({ theme }) => theme.text};
    background: ${({ theme }) => theme.widget.background};
    border: 1px solid ${({ theme }) => theme.border_color};
    cursor: pointer;
  }
  .active .page-link {
    background: ${({ theme }) => theme.primary || '#0d8a6a'};
    border-color: ${({ theme }) => theme.primary || '#0d8a6a'};
    color: #fff;
  }
  .disabled .page-link {
    opacity: 0.45;
    pointer-events: none;
  }
`;
const PageInfo = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  color: ${({ theme }) => theme.text_muted || theme.text};
`;
const PageSizeLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${({ theme }) => theme.text_muted || theme.text};

  select {
    padding: 5px 8px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.border_color};
    background: ${({ theme }) => theme.widget.background};
    color: ${({ theme }) => theme.text};
  }
`;
const ErrorBox = styled.div`
  padding: 16px;
  border-radius: 12px;
  border: 1px solid rgba(225, 29, 72, 0.25);
  background: rgba(225, 29, 72, 0.06);
  color: ${({ theme }) => theme.text};
`;
